import { useCallback, useEffect, useState } from 'react'
import type { TablesInsert, TablesUpdate } from '../../../supabase/functions/_shared/database.types'
import { supabase } from '../lib/supabase'
import type { DisciplineSplit, RaceResult } from '../types/raceResults'

interface UseRaceResultsReturn {
  raceResults: RaceResult[]
  loading: boolean
  error: string | null
  addRaceResult: (raceResult: Omit<RaceResult, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>
  updateRaceResult: (id: string, raceResult: Omit<RaceResult, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>
  deleteRaceResult: (id: string) => Promise<void>
  refreshRaceResults: () => Promise<void>
}

type RaceResultCreate = TablesInsert<'race_results'>
type RaceResultUpdate = TablesUpdate<'race_results'>

// Helper function to convert empty strings and undefined to null for database storage
const toDbValue = (value: string | number | undefined): string | number | null => {
  if (value === undefined || value === '' || (typeof value === 'string' && value.trim() === '')) {
    return null
  }
  return value
}

export const useRaceResults = (athleteId: string): UseRaceResultsReturn => {
  const [raceResults, setRaceResults] = useState<RaceResult[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadRaceResults = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const { data, error: supabaseError } = await supabase
        .from('race_results')
        .select('*')
        .eq('user_id', athleteId)
        .order('date', { ascending: false })

      if (supabaseError) {
        throw new Error(supabaseError.message)
      }

      // Transform Supabase data to our RaceResult type
      const transformedData: RaceResult[] = (data || []).map((row) => {
        // Parse splits from JSON if available, otherwise create from legacy fields
        let splits: DisciplineSplit[] = []
        if (row.splits && typeof row.splits === 'string') {
          try {
            splits = JSON.parse(row.splits)
          } catch (e) {
            console.warn('Failed to parse splits JSON:', e)
          }
        }

        return {
          id: row.id,
          userId: row.user_id,
          name: row.name,
          date: row.date,
          location: row.location ?? '',
          raceType: row.race_type,
          status: row.status,
          totalTime: row.total_time || 0,
          overallRank: row.overall_rank || 0,
          totalParticipants: row.total_participants || 0,
          overallPercentile: row.overall_percentile || 0,
          ageGroup: row.age_group || '',
          ageGroupRank: row.age_group_rank || 0,
          ageGroupParticipants: row.age_group_participants || 0,
          ageGroupPercentile: row.age_group_percentile || 0,
          splits: splits,
          weather: row.weather ?? '',
          temperature: row.temperature,
          notes: row.notes,
          raceUrl: row.race_url,
          createdAt: row.created_at,
          updatedAt: row.updated_at,
        } as RaceResult
      })

      setRaceResults(transformedData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load race results')
      console.error('Error loading race results:', err)
    } finally {
      setLoading(false)
    }
  }, [athleteId])

  const addRaceResult = useCallback(
    async (raceResultData: Omit<RaceResult, 'id' | 'createdAt' | 'updatedAt'>) => {
      try {
        setError(null)

        // Transform our RaceResult type to Supabase format, converting empty strings to null
        const supabaseData: RaceResultCreate = {
          user_id: raceResultData.userId,
          name: raceResultData.name,
          date: raceResultData.date,
          location: raceResultData.location?.trim(),
          race_type: raceResultData.raceType,
          status: raceResultData.status,
          total_time: raceResultData.totalTime || null,
          overall_rank: raceResultData.overallRank || null,
          total_participants: raceResultData.totalParticipants || null,
          overall_percentile: raceResultData.overallPercentile || null,
          age_group: raceResultData.ageGroup?.trim(),
          age_group_rank: raceResultData.ageGroupRank || null,
          age_group_participants: raceResultData.ageGroupParticipants || null,
          age_group_percentile: raceResultData.ageGroupPercentile || null,
          splits: JSON.stringify(raceResultData.splits || []),
          weather: raceResultData.weather?.trim(),
          temperature: raceResultData.temperature || null,
          notes: raceResultData.notes?.trim(),
          race_url: raceResultData.raceUrl?.trim(),
        }

        const { error: supabaseError } = await supabase.from('race_results').insert(supabaseData)

        if (supabaseError) {
          throw new Error(supabaseError.message)
        }

        // Refresh the data after successful insert
        await loadRaceResults()
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to add race result'
        setError(errorMessage)
        console.error('Error adding race result:', err)
        throw new Error(errorMessage)
      }
    },
    [loadRaceResults]
  )

  const updateRaceResult = useCallback(
    async (id: string, raceResultData: Omit<RaceResult, 'id' | 'createdAt' | 'updatedAt'>) => {
      try {
        setError(null)

        // Transform our RaceResult type to Supabase format
        const supabaseData = {
          user_id: raceResultData.userId,
          name: raceResultData.name,
          date: raceResultData.date,
          location: raceResultData.location,
          race_type: raceResultData.raceType,
          status: raceResultData.status,
          total_time: raceResultData.totalTime,
          overall_rank: raceResultData.overallRank,
          total_participants: raceResultData.totalParticipants,
          overall_percentile: raceResultData.overallPercentile,
          age_group: raceResultData.ageGroup,
          age_group_rank: raceResultData.ageGroupRank,
          age_group_participants: raceResultData.ageGroupParticipants,
          age_group_percentile: raceResultData.ageGroupPercentile,
          splits: JSON.stringify(raceResultData.splits || []),
          weather: raceResultData.weather,
          temperature: raceResultData.temperature,
          notes: raceResultData.notes,
          race_url: raceResultData.raceUrl,
          updated_at: new Date().toISOString(),
        }

        const { error: supabaseError } = await supabase.from('race_results').update(supabaseData).eq('id', id)

        if (supabaseError) {
          throw new Error(supabaseError.message)
        }

        // Refresh the data after successful update
        await loadRaceResults()
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update race result'
        setError(errorMessage)
        console.error('Error updating race result:', err)
        throw new Error(errorMessage)
      }
    },
    [loadRaceResults]
  )

  const deleteRaceResult = useCallback(
    async (id: string) => {
      try {
        setError(null)

        const { error: supabaseError } = await supabase.from('race_results').delete().eq('id', id)

        if (supabaseError) {
          throw new Error(supabaseError.message)
        }

        // Refresh the data after successful delete
        await loadRaceResults()
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete race result'
        setError(errorMessage)
        console.error('Error deleting race result:', err)
        throw new Error(errorMessage)
      }
    },
    [loadRaceResults]
  )

  const refreshRaceResults = useCallback(async () => {
    await loadRaceResults()
  }, [loadRaceResults])

  // Load race results on mount and when athleteId changes
  useEffect(() => {
    if (athleteId) {
      loadRaceResults()
    }
  }, [athleteId, loadRaceResults])

  return {
    raceResults,
    loading,
    error,
    addRaceResult,
    updateRaceResult,
    deleteRaceResult,
    refreshRaceResults,
  }
}
